import { WebSocketStatus } from '@hocuspocus/provider';
import clsx from 'clsx';
import { AlertTriangleIcon, LoaderCircleIcon } from 'lucide-react';
import { useCallback } from 'react';

import { InfoModal } from '../../../components/modals/info/infoModal.component';
import type { YJSProviderHookResult } from '../../../editor/hooks/useYjsProvider.hook';
import { useModals } from '../../../hooks/useModals.hook';

export const DocumentSyncStatus: React.FC<{
    provider: YJSProviderHookResult;
    className?: string;
    labelClass?: string;
}> = ({ provider, className, labelClass }) => {
    const { showModal } = useModals();
    const { connectionStatus, isSynced, isSaved, isChanged, wasSynced } = provider;

    const handleOfflineClick = useCallback(() => {
        // Force reconnect
        provider.provider?.connect();

        showModal('doc-offline', InfoModal, {
            message: 'Aplikace je offline, probíhá opětovné připojení k serveru. Vaše změny budou synchronizovány, jakmile se připojení obnoví.'
        });
    }, [ showModal, provider.provider ]);

    if (!wasSynced) {
        return null;
    }

    let content: JSX.Element | null = null;

    const _labelClass = clsx(
        'overflow-hidden overflow-ellipsis whitespace-nowrap',
        labelClass
    );

    if (connectionStatus === WebSocketStatus.Connected) {
        if (isSynced && (isSaved || !isChanged)) {
            content = (
                <span
                    className='flex flex-row items-center gap-1 overflow-hidden text-sm text-lime-800 font-medium px-1.5'
                    title='Všechny změny byly uloženy.'
                >
                    <span className='block w-[24px] h-[24px] flex-shrink-0 text-lime-800'>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5C8.12489 5 6.5 8 6 10C4 10.5 2 11.7 2 14.5C2 17.5 4.66667 18.8333 6 19H18C19.5 19 22 18.3382 22 15.5C22 12.6618 19.5 12 18 12C18.5 9.5 16.7213 5 12 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M15 11L10.1875 16L8 13.7273" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                </span>
            );
        } else if (isSynced && !isSaved && isChanged) {
            content = (
                <span
                    className='flex flex-row items-center gap-1 overflow-hidden text-sm text-gray-600 font-medium px-2'
                    title='Probíhá ukládání změn...'
                >
                    <LoaderCircleIcon size={18} className='text-gray-500 animate-spin flex-shrink-0' />
                    <span className={_labelClass}>Ukládání změn...</span>
                </span>
            );
        } else if (!isSynced) {
            content = (
                <span
                    className='flex flex-row items-center gap-1 overflow-hidden text-sm text-gray-600 font-medium px-2'
                    title='Probíhá synchronizace změn...'
                >
                    <LoaderCircleIcon size={18} className='text-gray-500 animate-spin flex-shrink-0' />
                    <span className={_labelClass}>Synchronizace změn...</span>
                </span>
            );
        } else {
            content = null;
        }
    } else {
        content = (
            <button
                className='flex flex-row items-center gap-1 overflow-hidden text-sm text-orange-600 font-medium p-2 xl:py-2 xl:px-3 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200'
                title='Aplikace je offline. Klikněte pro opětovné připojení.'
                onClick={handleOfflineClick}
            >
                <AlertTriangleIcon size={20} className='text-orange-600 flex-shrink-0' />
                {connectionStatus === WebSocketStatus.Disconnected && (
                    <span className={_labelClass}>Offline</span>
                )}
                {connectionStatus === WebSocketStatus.Connecting && (
                    <span className={_labelClass}>Připojování k serveru...</span>
                )}
            </button>
        );
    }

    return (
        <div className={clsx(
            // 'flex-shrink flex flex-row',
            className
        )}>
            {content}
        </div>
    );
};
